/**
 * Replay a Translation (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app (02-replay-button.png
 * from v0.41.1; that view has not changed since).
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Replay a Translation",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "With Keep spoken translations for replay on, Sokuji keeps the spoken translations, so you can play one again, for example when the other side missed it. Your lines are kept when Translation the other side hears is on, and the other side’s when Translation I hear is on."
  },
  "steps": [
    {
      "title": "Switch it on",
      "content": "In Settings, switch on <strong>Keep spoken translations for replay</strong>, the last row of the <strong>Speech</strong> block (Quick settings, or Advanced on the <strong>Audio</strong> tab). It is off by default, and you can change it during a session. It is greyed while nothing is spoken.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Play a line again",
      "content": "A play button appears next to the translation of each spoken line: yours, and the other side’s when Translation I hear is on. Click it to hear that translation again; one plays at a time.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "A replay plays on your side only, never into the virtual microphone, so the people in your meeting do not hear it again. Your line plays on the device of I hear it too and the other side’s on the device of Translation I hear. Face-to-face, your line replays in the other person’s earbud.",
      "The audio is kept in memory only. It is gone when you clear the conversation or close Sokuji; nothing is written to disk.",
      "Replay is in the expanded view only, and a line that was not spoken has nothing to replay."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Why don’t I see the play button?",
        "answer": "Check that Keep spoken translations for replay was on when the line was spoken, that the line was spoken (Translation the other side hears for your lines, Translation I hear for the other side’s), and that you are not in compact view."
      },
      {
        "question": "Why is the play button greyed?",
        "answer": "In the desktop app, replay is off while Other’s audio captures all system sound, because the replay would be translated again. Pick one application as Other’s audio source instead."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "重播翻译",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "开启“保留译音以便回放”后，Sokuji 会保留朗读出来的翻译，以便你再次播放某一条，例如在对方没听清时。开启“对方听到的翻译”时会保留你的发言，开启“我听到的翻译”时会保留对方的发言。"
  },
  "steps": [
    {
      "title": "开启",
      "content": "在设置中开启<strong>保留译音以便回放</strong>，它是<strong>语音</strong>区块的最后一行（“快捷”设置中，或“高级”的<strong>音频</strong>标签页）。它默认关闭，会话期间也可以更改。没有任何朗读时它是灰色的。",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "再次播放某条发言",
      "content": "每条朗读过的发言的译文旁边都会出现一个播放按钮：你的发言，以及开启“我听到的翻译”时对方的发言。点击它即可再次听到该条翻译；一次只播放一条。",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "重播只在你这边播放，绝不会进入虚拟麦克风，会议中的人不会再听一遍。你的发言在“我也听”的设备上播放，对方的发言在“我听到的翻译”的设备上播放。面对面时，你的发言会在对方的耳机里重播。",
      "音频只保存在内存中。清除对话或关闭 Sokuji 后就会消失；不会写入磁盘。",
      "重播只在展开视图中可用，没有朗读过的发言没有可重播的内容。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "为什么看不到播放按钮？",
        "answer": "检查说出该条发言时“保留译音以便回放”是否已开启，该条发言是否被朗读（你的发言看“对方听到的翻译”，对方的发言看“我听到的翻译”），以及你是否处于紧凑视图。"
      },
      {
        "question": "为什么播放按钮是灰色的？",
        "answer": "在桌面应用中，当对方音频正在捕获整个系统的声音时，重播会关闭，因为重播的声音会被再次翻译。请改为把某一个应用设为对方音频的来源。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "翻訳を再生し直す",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "「再生用に音声を保持」をオンにすると、Sokuji は読み上げた翻訳を保持するので、相手が聞き逃したときなどにもう一度再生できます。あなたの発言は「相手に聞こえる翻訳」がオンのとき、相手の発言は「自分に聞こえる翻訳」がオンのときに保持されます。"
  },
  "steps": [
    {
      "title": "オンにする",
      "content": "設定で、<strong>音声</strong>ブロックの最後の行にある<strong>再生用に音声を保持</strong>をオンにします（「クイック」設定、または「詳細」の<strong>オーディオ</strong>タブ）。既定ではオフで、セッション中でも変更できます。何も読み上げない設定のときはグレー表示になります。",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "発言をもう一度再生する",
      "content": "読み上げた各発言の訳文の横に再生ボタンが表示されます。あなたの発言のほか、「自分に聞こえる翻訳」がオンなら相手の発言にも表示されます。クリックするとその翻訳をもう一度聞けます。一度に再生されるのは 1 つだけです。",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "再生音声はあなたの側だけで鳴り、仮想マイクには決して入らないので、会議の参加者にもう一度聞こえることはありません。あなたの発言は「自分にも再生」のデバイスで、相手の発言は「自分に聞こえる翻訳」のデバイスで再生されます。対面では、あなたの発言は相手のイヤホンで再生されます。",
      "音声はメモリ上にのみ保持されます。会話をクリアするか Sokuji を閉じると消え、ディスクには何も書き込まれません。",
      "再生できるのは展開表示のときだけです。読み上げられなかった発言には、再生できる音声がありません。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "再生ボタンが表示されないのはなぜですか？",
        "answer": "その発言のときに「再生用に音声を保持」がオンだったか、その発言が読み上げられたか（あなたの発言は「相手に聞こえる翻訳」、相手の発言は「自分に聞こえる翻訳」）、コンパクト表示になっていないかを確認してください。"
      },
      {
        "question": "再生ボタンがグレーになっているのはなぜですか？",
        "answer": "デスクトップアプリでは、相手の音声がシステム全体の音をキャプチャしている間、再生はオフになります。再生した音がもう一度翻訳されてしまうためです。代わりに、相手の音声のソースとして 1 つのアプリを選んでください。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "번역 다시 재생하기",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "“다시 듣기용으로 번역 음성 보관”을 켜면 Sokuji가 음성으로 읽어 준 번역을 보관하므로, 상대방이 놓쳤을 때처럼 다시 재생할 수 있습니다. 내 발화는 “상대방에게 들리는 번역”이 켜져 있을 때, 상대방의 발화는 “내가 듣는 번역”이 켜져 있을 때 보관됩니다."
  },
  "steps": [
    {
      "title": "켜기",
      "content": "설정에서 <strong>음성</strong> 블록의 마지막 행인 <strong>다시 듣기용으로 번역 음성 보관</strong>을 켜세요(“빠른” 설정 또는 “고급”의 <strong>오디오</strong> 탭). 기본값은 꺼짐이며, 세션 중에도 바꿀 수 있습니다. 음성으로 읽어 주는 것이 없으면 회색으로 표시됩니다.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "발화 다시 재생",
      "content": "음성으로 읽어 준 각 발화의 번역 옆에 재생 버튼이 나타납니다. 내 발화에 나타나고, “내가 듣는 번역”이 켜져 있으면 상대방의 발화에도 나타납니다. 클릭하면 그 번역을 다시 들을 수 있으며, 한 번에 하나씩만 재생됩니다.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "다시 재생한 음성은 내 쪽에서만 나오고 가상 마이크로는 절대 들어가지 않으므로, 회의 참가자들이 다시 듣지 않습니다. 내 발화는 “나도 듣기”의 장치로, 상대방의 발화는 “내가 듣는 번역”의 장치로 재생됩니다. 대면 대화에서는 내 발화가 상대방의 이어버드에서 다시 재생됩니다.",
      "오디오는 메모리에만 보관됩니다. 대화를 지우거나 Sokuji를 닫으면 사라지며, 디스크에는 아무것도 기록되지 않습니다.",
      "다시 재생은 확장 보기에서만 할 수 있으며, 음성으로 읽어 주지 않은 발화는 다시 재생할 내용이 없습니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "재생 버튼이 보이지 않는 이유는 무엇인가요?",
        "answer": "그 발화를 했을 때 “다시 듣기용으로 번역 음성 보관”이 켜져 있었는지, 그 발화를 음성으로 읽어 주었는지(내 발화는 “상대방에게 들리는 번역”, 상대방의 발화는 “내가 듣는 번역”), 간략 보기가 아닌지 확인하세요."
      },
      {
        "question": "재생 버튼이 회색으로 표시되는 이유는 무엇인가요?",
        "answer": "데스크톱 앱에서는 상대방 오디오가 시스템 전체 소리를 캡처하는 동안 다시 재생이 꺼집니다. 다시 재생한 소리가 또 번역되기 때문입니다. 대신 상대방 오디오 소스로 앱 하나를 고르세요."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Volver a reproducir una traducción",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Con Conservar las traducciones habladas para reproducirlas activado, Sokuji guarda las traducciones habladas para que puedas volver a reproducir una, por ejemplo cuando la otra parte no la oyó bien. Tus líneas se guardan cuando Traducción que oye la otra parte está activada, y las de la otra parte cuando Traducción que yo oigo está activada."
  },
  "steps": [
    {
      "title": "Actívalo",
      "content": "En Configuración, activa <strong>Conservar las traducciones habladas para reproducirlas</strong>, la última fila del bloque <strong>Voz</strong> (Rápido o, en Avanzado, la pestaña <strong>Audio</strong>). Está desactivado de forma predeterminada y puedes cambiarlo durante una sesión. Aparece en gris mientras no se lee nada en voz alta.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Vuelve a reproducir una línea",
      "content": "Junto a la traducción de cada línea leída en voz alta aparece un botón de reproducción: en las tuyas y, cuando Traducción que yo oigo está activada, en las de la otra parte. Haz clic en él para volver a oír esa traducción; se reproduce una cada vez.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "La reproducción suena solo de tu lado, nunca en el micrófono virtual, así que los participantes de tu reunión no la vuelven a oír. Tu línea suena en el dispositivo de Yo también la oigo y la de la otra parte en el de Traducción que yo oigo. En una conversación cara a cara, tu línea se vuelve a reproducir en el auricular de la otra persona.",
      "El audio solo se guarda en memoria. Desaparece cuando borras la conversación o cierras Sokuji; no se escribe nada en el disco.",
      "La reproducción solo está en la vista expandida, y una línea que no se leyó en voz alta no tiene nada que reproducir."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Por qué no veo el botón de reproducción?",
        "answer": "Comprueba que Conservar las traducciones habladas para reproducirlas estaba activado cuando se dijo la línea, que la línea se leyó en voz alta (Traducción que oye la otra parte para tus líneas, Traducción que yo oigo para las de la otra parte) y que no estás en la vista compacta."
      },
      {
        "question": "¿Por qué aparece en gris el botón de reproducción?",
        "answer": "En la aplicación de escritorio, la reproducción está desactivada mientras el Audio del otro captura todo el sonido del sistema, porque se volvería a traducir. Elige en su lugar una aplicación concreta como fuente del Audio del otro."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Réécouter une traduction",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Avec Conserver les traductions parlées pour les réécouter activé, Sokuji conserve les traductions lues à voix haute, pour que vous puissiez en réécouter une, par exemple quand votre interlocuteur l’a manquée. Vos lignes sont conservées quand Traduction entendue par l'autre est activée, et celles de votre interlocuteur quand Traduction que j'entends est activée."
  },
  "steps": [
    {
      "title": "Activez-le",
      "content": "Dans les Paramètres, activez <strong>Conserver les traductions parlées pour les réécouter</strong>, la dernière ligne du bloc <strong>Voix</strong> (Rapide, ou Avancé sous l’onglet <strong>Audio</strong>). Il est désactivé par défaut, et vous pouvez le changer pendant une session. Il est grisé tant que rien n’est lu à voix haute.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Réécoutez une ligne",
      "content": "Un bouton de lecture apparaît à côté de la traduction de chaque ligne lue à voix haute : les vôtres, et celles de votre interlocuteur quand Traduction que j'entends est activée. Cliquez dessus pour réentendre cette traduction ; une seule est lue à la fois.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "La réécoute n’est lue que de votre côté, jamais dans le microphone virtuel : les participants de votre réunion ne l’entendent donc pas à nouveau. Votre ligne est lue sur le périphérique de Je l'entends aussi, et celle de votre interlocuteur sur celui de Traduction que j'entends. En face à face, votre ligne est rejouée dans l’écouteur de l’autre personne.",
      "L’audio est conservé uniquement en mémoire. Il disparaît quand vous effacez la conversation ou fermez Sokuji ; rien n’est écrit sur le disque.",
      "La réécoute n’existe que dans la vue développée, et une ligne qui n’a pas été lue à voix haute n’a rien à réécouter."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Pourquoi ne vois-je pas le bouton de lecture ?",
        "answer": "Vérifiez que Conserver les traductions parlées pour les réécouter était activé quand la ligne a été prononcée, que la ligne a bien été lue à voix haute (Traduction entendue par l'autre pour vos lignes, Traduction que j'entends pour celles de votre interlocuteur) et que vous n’êtes pas en vue compacte."
      },
      {
        "question": "Pourquoi le bouton de lecture est-il grisé ?",
        "answer": "Dans l’application de bureau, la réécoute est désactivée tant que l’Audio de l'autre capture tout le son du système, car la réécoute serait traduite de nouveau. Choisissez plutôt une seule application comme source d’Audio de l'autre."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "إعادة تشغيل ترجمة",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "عند تفعيل «الاحتفاظ بالترجمات المنطوقة لإعادة التشغيل»، يحتفظ Sokuji بالترجمات المنطوقة، فيمكنك تشغيل إحداها مرة أخرى، مثلًا عندما يفوت الطرف الآخر سماعها. يُحتفظ بجملك عندما تكون «الترجمة التي يسمعها الطرف الآخر» مفعّلة، وبجمل الطرف الآخر عندما تكون «الترجمة التي أسمعها» مفعّلة."
  },
  "steps": [
    {
      "title": "فعّله",
      "content": "في الإعدادات، فعّل <strong>الاحتفاظ بالترجمات المنطوقة لإعادة التشغيل</strong>، وهو آخر صف في قسم <strong>الكلام</strong> (إعدادات «سريع»، أو «متقدم» ضمن علامة التبويب <strong>الصوت</strong>). يكون متوقفًا افتراضيًا، ويمكنك تغييره أثناء الجلسة. ويكون باللون الرمادي ما دام لا يُنطق شيء.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "شغّل جملة مرة أخرى",
      "content": "يظهر زر تشغيل بجانب ترجمة كل جملة منطوقة: جملك، وجمل الطرف الآخر عندما تكون «الترجمة التي أسمعها» مفعّلة. انقر عليه لتسمع تلك الترجمة مرة أخرى؛ تُشغَّل ترجمة واحدة في كل مرة.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "لا تُشغَّل إعادة التشغيل إلا في جانبك، ولا تدخل الميكروفون الافتراضي أبدًا، لذا لا يسمعها المشاركون في اجتماعك مرة أخرى. تُشغَّل جملتك على جهاز «أسمعها أنا أيضًا»، وجملة الطرف الآخر على جهاز «الترجمة التي أسمعها». وفي المحادثة وجهًا لوجه، تُعاد جملتك في سماعة أذن الشخص الآخر.",
      "يُحفظ الصوت في الذاكرة فقط. يختفي عند مسح المحادثة أو إغلاق Sokuji؛ ولا يُكتب أي شيء على القرص.",
      "إعادة التشغيل متاحة في العرض الموسع فقط، والجملة التي لم تُنطق ليس فيها ما يُعاد تشغيله."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "لماذا لا أرى زر التشغيل؟",
        "answer": "تحقّق من أن «الاحتفاظ بالترجمات المنطوقة لإعادة التشغيل» كان مفعّلًا عند نطق الجملة، وأن الجملة نُطقت فعلًا («الترجمة التي يسمعها الطرف الآخر» لجملك، و«الترجمة التي أسمعها» لجمل الطرف الآخر)، وأنك لست في العرض المضغوط."
      },
      {
        "question": "لماذا يظهر زر التشغيل باللون الرمادي؟",
        "answer": "في تطبيق سطح المكتب، تتوقف إعادة التشغيل ما دام صوت الآخر يلتقط كل صوت النظام، لأن إعادة التشغيل ستُترجم مرة أخرى. اختر تطبيقًا واحدًا مصدرًا لصوت الآخر بدلًا من ذلك."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Reproduzir uma tradução novamente",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Com Manter traduções faladas para reprodução ativado, o Sokuji guarda as traduções faladas, para que você possa reproduzir uma novamente, por exemplo quando a outra pessoa não ouviu direito. As suas falas são guardadas quando Tradução que o outro lado ouve está ativada, e as da outra pessoa quando Tradução que eu ouço está ativada."
  },
  "steps": [
    {
      "title": "Ative a opção",
      "content": "Em Configurações, ative <strong>Manter traduções faladas para reprodução</strong>, a última linha do bloco <strong>Voz</strong> (Rápido ou, em Avançado, a aba <strong>Áudio</strong>). A opção vem desativada por padrão, e você pode alterá-la durante uma sessão. Ela fica esmaecida enquanto nada é falado.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Reproduza uma fala de novo",
      "content": "Um botão de reprodução aparece ao lado da tradução de cada fala lida em voz alta: as suas e, quando Tradução que eu ouço está ativada, as da outra pessoa. Clique nele para ouvir essa tradução de novo; toca uma de cada vez.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "A reprodução toca só do seu lado, nunca no microfone virtual, então os participantes da reunião não a ouvem de novo. A sua fala toca no dispositivo de Eu também ouço, e a da outra pessoa no de Tradução que eu ouço. Em uma conversa cara a cara, a sua fala é reproduzida no fone da outra pessoa.",
      "O áudio fica guardado apenas na memória. Ele some quando você limpa a conversa ou fecha o Sokuji; nada é gravado no disco.",
      "A reprodução só existe na visualização expandida, e uma fala que não foi lida em voz alta não tem o que reproduzir."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Por que não vejo o botão de reprodução?",
        "answer": "Verifique se Manter traduções faladas para reprodução estava ativado quando a fala foi dita, se a fala foi lida em voz alta (Tradução que o outro lado ouve para as suas falas, Tradução que eu ouço para as da outra pessoa) e se você não está na visualização compacta."
      },
      {
        "question": "Por que o botão de reprodução aparece esmaecido?",
        "answer": "No aplicativo de desktop, a reprodução fica desativada enquanto o Áudio do outro captura todo o som do sistema, porque a reprodução seria traduzida de novo. Escolha um aplicativo específico como fonte do Áudio do outro."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Повторное воспроизведение перевода",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Когда включено «Сохранять озвученные переводы для повтора», Sokuji сохраняет озвученные переводы, чтобы любой из них можно было воспроизвести снова, например если собеседник не расслышал. Ваши строки сохраняются, когда включён «Перевод, который слышит собеседник», а строки собеседника — когда включён «Перевод, который слышу я»."
  },
  "steps": [
    {
      "title": "Включите его",
      "content": "В настройках включите <strong>Сохранять озвученные переводы для повтора</strong> — последнюю строку блока <strong>Речь</strong> (в режиме «Быстрый» или в режиме «Расширенный» на вкладке <strong>Аудио</strong>). По умолчанию параметр выключен, и его можно менять во время сеанса. Пока ничего не озвучивается, он серый.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Воспроизведите строку снова",
      "content": "Рядом с переводом каждой озвученной строки появляется кнопка воспроизведения: у ваших строк, а у строк собеседника — когда включён «Перевод, который слышу я». Нажмите её, чтобы снова услышать этот перевод; одновременно воспроизводится только один.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Повтор звучит только у вас и никогда не поступает на виртуальный микрофон, так что участники встречи его снова не слышат. Ваша строка звучит на устройстве «Я тоже слышу», а строка собеседника — на устройстве «Перевод, который слышу я». При разговоре лицом к лицу ваша строка повторяется в наушнике собеседника.",
      "Аудио хранится только в памяти. Оно исчезает, когда вы очищаете разговор или закрываете Sokuji; на диск ничего не записывается.",
      "Повтор доступен только в развёрнутом виде, а у строки, которая не была озвучена, повторять нечего."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Почему я не вижу кнопку воспроизведения?",
        "answer": "Проверьте, что «Сохранять озвученные переводы для повтора» было включено, когда строка прозвучала, что строка была озвучена («Перевод, который слышит собеседник» для ваших строк, «Перевод, который слышу я» для строк собеседника) и что вы не в компактном виде."
      },
      {
        "question": "Почему кнопка воспроизведения серая?",
        "answer": "В настольном приложении повтор отключён, пока аудио собеседника захватывает весь системный звук, потому что повтор был бы переведён снова. Выберите вместо этого источником аудио собеседника одно приложение."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Eine Übersetzung erneut abspielen",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Mit „Gesprochene Übersetzungen zur Wiedergabe behalten“ behält Sokuji die gesprochenen Übersetzungen, sodass Sie eine davon erneut abspielen können, zum Beispiel wenn Ihr Gegenüber sie verpasst hat. Ihre Zeilen werden behalten, wenn „Übersetzung, die das Gegenüber hört“ eingeschaltet ist, die Ihres Gegenübers, wenn „Übersetzung, die ich höre“ eingeschaltet ist."
  },
  "steps": [
    {
      "title": "Einschalten",
      "content": "Schalten Sie in den Einstellungen <strong>Gesprochene Übersetzungen zur Wiedergabe behalten</strong> ein, die letzte Zeile im Bereich <strong>Sprache</strong> (Ansicht „Schnell“ oder „Erweitert“ im Tab <strong>Audio</strong>). Die Option ist standardmäßig aus, und Sie können sie während einer Sitzung ändern. Solange nichts gesprochen wird, ist sie ausgegraut.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Eine Zeile erneut abspielen",
      "content": "Neben der Übersetzung jeder gesprochenen Zeile erscheint eine Wiedergabe-Schaltfläche: bei Ihren Zeilen und, wenn „Übersetzung, die ich höre“ eingeschaltet ist, bei denen Ihres Gegenübers. Klicken Sie darauf, um diese Übersetzung erneut zu hören; es wird immer nur eine gleichzeitig abgespielt.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Die Wiedergabe läuft nur auf Ihrer Seite, nie in das virtuelle Mikrofon, die Teilnehmenden Ihres Meetings hören sie also nicht erneut. Ihre Zeile wird auf dem Gerät von „Ich höre es auch“ abgespielt, die Ihres Gegenübers auf dem Gerät von „Übersetzung, die ich höre“. Von Angesicht zu Angesicht wird Ihre Zeile im Ohrhörer der anderen Person wiedergegeben.",
      "Das Audio wird nur im Arbeitsspeicher gehalten. Es ist weg, wenn Sie die Unterhaltung löschen oder Sokuji schließen; auf die Festplatte wird nichts geschrieben.",
      "Die Wiedergabe gibt es nur in der erweiterten Ansicht, und eine Zeile, die nicht gesprochen wurde, hat nichts zum erneuten Abspielen."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Warum sehe ich die Wiedergabe-Schaltfläche nicht?",
        "answer": "Prüfen Sie, ob „Gesprochene Übersetzungen zur Wiedergabe behalten“ eingeschaltet war, als die Zeile gesprochen wurde, ob die Zeile gesprochen wurde („Übersetzung, die das Gegenüber hört“ für Ihre Zeilen, „Übersetzung, die ich höre“ für die Ihres Gegenübers) und ob Sie sich nicht in der kompakten Ansicht befinden."
      },
      {
        "question": "Warum ist die Wiedergabe-Schaltfläche ausgegraut?",
        "answer": "In der Desktop-App ist die Wiedergabe deaktiviert, solange das Audio des Gegenübers den gesamten Systemton aufnimmt, weil die Wiedergabe sonst erneut übersetzt würde. Wählen Sie stattdessen eine einzelne Anwendung als Quelle für das Audio des Gegenübers."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Повторне відтворення перекладу",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Коли ввімкнено «Зберігати озвучені переклади для повтору», Sokuji зберігає озвучені переклади, щоб будь-який із них можна було відтворити знову, наприклад якщо співрозмовник не розчув. Ваші рядки зберігаються, коли ввімкнено «Переклад, який чує співрозмовник», а рядки співрозмовника — коли ввімкнено «Переклад, який чую я»."
  },
  "steps": [
    {
      "title": "Увімкніть його",
      "content": "У налаштуваннях увімкніть <strong>Зберігати озвучені переклади для повтору</strong> — останній рядок блоку <strong>Мовлення</strong> (у режимі «Швидкий» або в режимі «Розширений» на вкладці <strong>Аудіо</strong>). За замовчуванням параметр вимкнено, і його можна змінювати під час сеансу. Поки нічого не озвучується, він сірий.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Відтворіть рядок знову",
      "content": "Поруч із перекладом кожного озвученого рядка з’являється кнопка відтворення: біля ваших рядків, а біля рядків співрозмовника — коли ввімкнено «Переклад, який чую я». Натисніть її, щоб знову почути цей переклад; одночасно відтворюється лише один.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Повтор звучить лише у вас і ніколи не надходить на віртуальний мікрофон, тож учасники зустрічі не чують його знову. Ваш рядок звучить на пристрої «Я теж чую», а рядок співрозмовника — на пристрої «Переклад, який чую я». Під час розмови віч-на-віч ваш рядок повторюється в навушнику співрозмовника.",
      "Аудіо зберігається лише в пам’яті. Воно зникає, коли ви очищаєте розмову або закриваєте Sokuji; на диск нічого не записується.",
      "Повтор доступний лише в розгорнутому вигляді, а для рядка, який не було озвучено, повторювати нічого."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Чому я не бачу кнопку відтворення?",
        "answer": "Перевірте, що «Зберігати озвучені переклади для повтору» було ввімкнено, коли рядок прозвучав, що рядок було озвучено («Переклад, який чує співрозмовник» для ваших рядків, «Переклад, який чую я» для рядків співрозмовника) і що ви не в компактному вигляді."
      },
      {
        "question": "Чому кнопка відтворення сіра?",
        "answer": "У настільному застосунку повтор вимкнено, поки аудіо співрозмовника захоплює весь системний звук, бо повтор було б перекладено знову. Виберіть натомість джерелом аудіо співрозмовника один застосунок."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Riascoltare una traduzione",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Con Conserva le traduzioni parlate per la riproduzione attivo, Sokuji conserva le traduzioni lette ad alta voce, così puoi riascoltarne una, per esempio quando l’altra parte non l’ha sentita. Le tue righe vengono conservate quando Traduzione che sente l'altra parte è attiva, e quelle dell’altra parte quando Traduzione che sento io è attiva."
  },
  "steps": [
    {
      "title": "Attivalo",
      "content": "In Impostazioni, attiva <strong>Conserva le traduzioni parlate per la riproduzione</strong>, l’ultima riga del blocco <strong>Voce</strong> (Rapido o, in Avanzato, la scheda <strong>Audio</strong>). È disattivato per impostazione predefinita e puoi cambiarlo durante una sessione. È in grigio finché non viene letto nulla ad alta voce.",
      "screenshot": "01-keep-audio.png"
    },
    {
      "title": "Riascolta una riga",
      "content": "Accanto alla traduzione di ogni riga letta ad alta voce compare un pulsante di riproduzione: le tue righe, e quelle dell’altra parte quando Traduzione che sento io è attiva. Fai clic per riascoltare quella traduzione; se ne riproduce una alla volta.",
      "screenshot": "02-replay-button.png"
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Il riascolto si sente solo dalla tua parte, mai nel microfono virtuale, quindi i partecipanti alla riunione non lo sentono di nuovo. La tua riga viene riprodotta sul dispositivo di Lo sento anche io e quella dell’altra parte sul dispositivo di Traduzione che sento io. Nel faccia a faccia, la tua riga viene riascoltata nell’auricolare dell’altra persona.",
      "L’audio viene conservato solo in memoria. Scompare quando cancelli la conversazione o chiudi Sokuji; nulla viene scritto su disco.",
      "Il riascolto c’è solo nella vista espansa, e una riga che non è stata letta ad alta voce non ha nulla da riprodurre."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Perché non vedo il pulsante di riproduzione?",
        "answer": "Controlla che Conserva le traduzioni parlate per la riproduzione fosse attivo quando la riga è stata pronunciata, che la riga sia stata letta ad alta voce (Traduzione che sente l'altra parte per le tue righe, Traduzione che sento io per quelle dell’altra parte) e che tu non sia nella vista compatta."
      },
      {
        "question": "Perché il pulsante di riproduzione è in grigio?",
        "answer": "Nell’app desktop il riascolto è disattivato mentre l’Audio dell'altro cattura tutto l’audio di sistema, perché il riascolto verrebbe tradotto di nuovo. Scegli invece una sola applicazione come sorgente dell’Audio dell'altro."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureReplay() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/replay" />;
}
